import { useEffect, useRef, useCallback } from 'react';

/**
 * useFocusTrap - Traps keyboard focus within a container (for modals/dialogs)
 * When active, Tab/Shift+Tab cycles through focusable elements inside the container.
 * Escape key calls onEscape callback.
 * Restores focus to the previously focused element on unmount.
 *
 * Usage:
 *   const trapRef = useFocusTrap({ active: isOpen, onEscape: handleClose });
 *   <div ref={trapRef}> ... modal content ... </div>
 */
const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled]):not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

export default function useFocusTrap({ active = true, onEscape } = {}) {
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (!containerRef.current) return;

    if (e.key === 'Escape' && onEscape) {
      e.stopPropagation();
      onEscape();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter((el) => el.offsetParent !== null); // visible only

    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onEscape]);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Save current focus
    previousFocusRef.current = document.activeElement;

    // Focus the first focusable element inside the trap
    const focusable = containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length > 0) {
      // Small delay to let the modal render
      requestAnimationFrame(() => {
        focusable[0].focus();
      });
    } else {
      // If no focusable children, make container itself focusable
      containerRef.current.setAttribute('tabindex', '-1');
      containerRef.current.focus();
    }

    const container = containerRef.current;
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Restore previous focus
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [active, handleKeyDown]);

  return containerRef;
}
